/* Evol Vault pack-simulator UI (Gallery layout, October 2026).
   Drives the DOM rendered by section-evol-pack-simulator.liquid using the pure
   engine in evol-sim-engine.js. The page is a wall of sets (era filters +
   search); picking one opens a full-screen opening room where everything else
   happens. Room phases:
     config -> tearing -> revealing -> packsum -> (next pack | session) -> config
   Opening the room from a tile pushes a history entry (?set=<id>), so the
   browser back button closes it; arriving with ?set=<id> opens that room.

   The tear gesture: pointer-drag across the pack's foil strip (finger or
   cursor). Progress persists on early release and resumes. From 70% progress
   the pack edge glows in the colour of the best card inside (the anticipation
   tell); at 85% the strip flies off and the pack bursts open. An "Open pack"
   button always performs the same open (keyboard / reduced-motion path).
   All gesture visuals are rAF-batched transform/clip-path/opacity only. */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-ps-root]');
    if (!root || !window.EvolSimEngine) return;
    var Engine = window.EvolSimEngine;

    var cfgEl = document.getElementById(root.getAttribute('data-ps-config-id'));
    var cfg = null;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (err) { return; }
    if (!cfg || !cfg.sets || !cfg.sets.length) return;

    var els = {};
    root.querySelectorAll('[data-ps]').forEach(function (el) {
      els[el.getAttribute('data-ps')] = el;
    });
    var required = ['config', 'sets', 'products', 'start', 'stage', 'pack', 'room', 'roomclose',
      'glow', 'hint', 'open', 'cards', 'rail', 'controls', 'flipnext',
      'flipall', 'packsum', 'session', 'progress', 'fallback', 'retry', 'restart',
      'packart', 'stripB', 'alltime', 'resetstats', 'packsumBody', 'packsumTitle',
      'next', 'finish', 'sessionBody', 'shop', 'guide', 'again',
      'sound', 'yourpulls', 'yourpullsrow', 'share', 'packshop', 'sessionBest'];
    for (var r = 0; r < required.length; r++) if (!els[required[r]]) return;
    var sheenEl = els.sheen || null;

    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) root.classList.add('is-reduced');

    function setCfg(id) {
      for (var i = 0; i < cfg.sets.length; i++) if (cfg.sets[i].id === id) return cfg.sets[i];
      return cfg.sets[0];
    }

    /* ?set=<simId> presets the set (set pages link here with theirs) */
    var presetSet = null;
    try { presetSet = new URLSearchParams(location.search).get('set'); } catch (e) { /* old browser */ }
    if (presetSet) {
      var presetOk = false;
      for (var pi = 0; pi < cfg.sets.length; pi++) if (cfg.sets[pi].id === presetSet) presetOk = true;
      if (presetOk) cfg.defaultSet = presetSet; else presetSet = null;
    }

    /* ---------- state ---------- */
    var state = {
      phase: 'config',
      setId: cfg.defaultSet,
      product: setCfg(cfg.defaultSet).products[0],
      data: null,
      pools: null,
      packs: [],
      tells: [],
      packIdx: 0,
      flipIdx: 0,
      tearP: 0,
      session: null,
      busy: false
    };
    var dataCache = {};
    var cardNodes = [];

    /* rebuild the product tiles for the selected set (availability is per-set:
       Ascended Heroes has no booster box, so its tile list is shorter) */
    function renderProductTiles(sc) {
      els.products.textContent = '';
      els.products.setAttribute('data-count', String(sc.products.length));
      var keys = sc.products.map(function (p) { return p.key; });
      if (!state.product || keys.indexOf(state.product.key) === -1) state.product = sc.products[0];
      sc.products.forEach(function (p) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'evol-ps-prodtile';
        btn.setAttribute('data-packs', String(p.packs));
        btn.setAttribute('data-kind', p.key);
        btn.setAttribute('aria-pressed', String(p.key === state.product.key));
        btn.setAttribute('aria-label', p.label + ', ' + p.packs + (p.packs === 1 ? ' pack' : ' packs'));
        var lab = document.createElement('span');
        lab.className = 'evol-ps-prodtile__lab'; lab.textContent = p.label;
        var short = document.createElement('span');
        short.className = 'evol-ps-prodtile__short'; short.textContent = p.short || p.label;
        var sub = document.createElement('span');
        sub.className = 'evol-ps-prodtile__sub'; sub.textContent = p.packs === 1 ? '1 pack' : p.packs + ' packs';
        btn.appendChild(lab); btn.appendChild(short); btn.appendChild(sub);
        els.products.appendChild(btn);
      });
    }

    /* "~$409 ungraded" -> 409 (for sorting) / "~$409" (for chips) */
    function valNum(v) { var m = /([0-9][0-9,.]*)/.exec(v || ''); return m ? parseFloat(m[1].replace(/,/g, '')) : 0; }
    function valShort(v) { return String(v || '').replace(/\s*ungraded\s*$/i, ''); }
    /* the set's chase cards: highest market value first (rarer tier breaks ties) */
    function chaseList(data, n) {
      var chase = data.cards.filter(function (c) { return data.values[c[0]] && Engine.TIER_RANK[c[2]] >= Engine.TIER_RANK.ir; });
      chase.sort(function (a, b) { return (valNum(data.values[b[0]]) - valNum(data.values[a[0]])) || (Engine.TIER_RANK[b[2]] - Engine.TIER_RANK[a[2]]); });
      return chase.slice(0, n);
    }
    function chaseNode(data, c, withVal) {
      var cn = cardKey(data, c[0], c[3]); /* JP: productId; merged: source/number */
      var span = document.createElement('span');
      span.className = 'evol-ps-chase evol-chase-card tier-' + c[2] + glowCls(c[2]);
      span.setAttribute('data-full', c[4] || data.imgLarge.replace('{n}', cn));
      var val = data.values[c[0]];
      span.setAttribute('data-caption', c[1] + ' #' + c[0] + ' · ' + Engine.TIER_LABEL[c[2]] + (val ? ' · ' + val : ''));
      var img = document.createElement('img');
      img.src = c[4] || data.imgSmall.replace('{n}', cn); img.alt = c[1] + ', ' + Engine.TIER_LABEL[c[2]]; img.loading = 'lazy';
      img.width = 245; img.height = 342;
      span.appendChild(tiltWrap(img));
      if (withVal) {
        var v = document.createElement('span');
        v.className = 'evol-ps-chase__val';
        v.textContent = valShort(val);
        span.appendChild(v);
      }
      return span;
    }
    /* 1 in [lo, hi] packs -> chance of at least one in n packs, as display text.
       Ranges stay ranges; never a literal 100% (calculator display rules). */
    /* precision follows size: 25% / 4.5% / 0.14%; never a literal 100% */
    function pct(p) {
      var x = p * 100;
      if (x >= 99.5) return '>99%';
      if (x >= 10) return Math.round(x) + '%';
      if (x >= 1) return x.toFixed(1).replace(/\.0$/, '') + '%';
      if (x >= 0.01) return x.toFixed(2) + '%';
      return '<0.01%';
    }
    function chanceText(lo, hi, n) {
      var a = pct(1 - Math.pow(1 - 1 / hi, n)), b = pct(1 - Math.pow(1 - 1 / lo, n));
      if (a === b) return a;
      if (b === '>99%') return a + '+';            /* "95%+": at least 95% */
      return a.replace('%', '') + '–' + b;          /* "0.03–0.14%", "<0.01–0.05%" */
    }
    function oddsText(lo, hi) { return lo === hi ? '1 in ' + lo : '1 in ' + lo + '–' + hi; }
    function renderLadder(sc, data) {
      if (!els.ladder) return;
      els.ladder.textContent = '';
      var n = state.product ? state.product.packs : 1;
      var tiers = Object.keys(data.rates || {}).filter(function (t) { return Engine.TIER_RANK[t] >= Engine.TIER_RANK.asr; });
      tiers.sort(function (a, b) { return (Engine.TIER_RANK[b] - Engine.TIER_RANK[a]) || (data.rates[b][0] - data.rates[a][0]); });
      if (els.ladderhead) els.ladderhead.textContent = n === 1 ? 'Chance in one pack' : 'Chance across ' + n + ' packs · ' + (state.product.short || state.product.label);
      tiers.forEach(function (t) {
        var r = data.rates[t];
        var row = document.createElement('div');
        row.className = 'evol-ps-rung tier-' + t + glowCls(t);
        var name = document.createElement('span'); name.className = 'evol-ps-rung__tier'; name.textContent = Engine.TIER_LABEL[t] || t;
        var odds = document.createElement('span'); odds.className = 'evol-ps-rung__odds'; odds.textContent = oddsText(r[0], r[1]) + ' packs';
        var ch = document.createElement('span'); ch.className = 'evol-ps-rung__chance'; ch.textContent = chanceText(r[0], r[1], n);
        var bar = document.createElement('span'); bar.className = 'evol-ps-rung__bar';
        var fill = document.createElement('i');
        var pm = 1 - Math.pow(1 - 2 / (r[0] + r[1]), n);
        fill.style.width = Math.max(2, Math.min(100, pm * 100)).toFixed(1) + '%';
        bar.appendChild(fill);
        row.appendChild(name); row.appendChild(odds); row.appendChild(ch); row.appendChild(bar);
        els.ladder.appendChild(row);
      });
    }
    function bindText(key, txt) {
      root.querySelectorAll('[data-ps-bind="' + key + '"]').forEach(function (el) { el.textContent = txt; });
    }
    /* per-set detail in the room: chase strip, odds ladder, meta */
    function renderChaseRow(sc) {
      /* clear the previous set's odds and cards at once: they must never sit
         beside this set's name while its data loads */
      if (els.chaseinline) { els.chaseinline.textContent = ''; els.chaseinline.setAttribute('aria-busy', 'true'); }
      if (els.ladder) { els.ladder.textContent = ''; els.ladder.setAttribute('aria-busy', 'true'); }
      bindText('name', sc.name);
      bindText('date', sc.release || '');
      bindText('era', sc.eraName || '');
      loadSet(sc.id).then(function (entry) {
        if (state.setId !== sc.id) return; /* set changed mid-load */
        var data = entry.data;
        bindText('count', data.cards.length + ' cards');
        if (els.chaseinline) {
          els.chaseinline.textContent = '';
          var lim = parseInt(els.chaseinline.getAttribute('data-ps-max') || '5', 10);
          chaseList(data, lim).forEach(function (c) { els.chaseinline.appendChild(chaseNode(data, c, true)); });
          els.chaseinline.removeAttribute('aria-busy');
        }
        renderLadder(sc, data);
        if (els.ladder) els.ladder.removeAttribute('aria-busy');
      }).catch(function () {
        if (els.chaseinline) els.chaseinline.removeAttribute('aria-busy');
        if (els.ladder) els.ladder.removeAttribute('aria-busy');
      });
    }

    function renderSetSelection() {
      var sc = setCfg(state.setId);
      els.sets.querySelectorAll('[data-set]').forEach(function (btn) {
        btn.setAttribute('aria-checked', String(btn.getAttribute('data-set') === state.setId));
      });
      if (els.showpack) { els.showpack.src = sc.pack; els.showpack.alt = sc.name + ' booster pack'; }
      if (els.showname) els.showname.textContent = sc.name;
      els.packart.src = sc.pack;
      els.packart.alt = sc.name + ' booster pack';
      els.stripB.style.backgroundImage = "url('" + sc.pack + "')";
      if (sheenEl) {
        sheenEl.style.webkitMaskImage = "url('" + sc.pack + "')";
        sheenEl.style.maskImage = "url('" + sc.pack + "')";
      }
      renderProductTiles(sc);
      renderChaseRow(sc);
    }

    /* ---------- stats (localStorage, garbage-tolerant) ---------- */
    var STATS_KEY = 'evol-sim-stats-v1';
    function loadStats() {
      try {
        var s = JSON.parse(localStorage.getItem(STATS_KEY));
        if (s && s.v === 1 && typeof s.packs === 'object' && typeof s.hits === 'object' && typeof s.best === 'object') return s;
      } catch (err) { /* fall through */ }
      return { v: 1, packs: {}, hits: {}, best: {} };
    }
    function saveStats(s) {
      try { localStorage.setItem(STATS_KEY, JSON.stringify(s)); } catch (err) { /* private mode */ }
    }
    var stats = loadStats();
    function renderAlltime() {
      var total = 0, hits = 0;
      Object.keys(stats.packs).forEach(function (k) { total += stats.packs[k]; });
      Object.keys(stats.hits).forEach(function (k) {
        Object.keys(stats.hits[k]).forEach(function (t) { hits += stats.hits[k][t]; });
      });
      els.alltime.hidden = total === 0;
      els.resetstats.hidden = total === 0;
      if (total > 0) els.alltime.textContent = 'All-time on this device: ' + total + (total === 1 ? ' pack' : ' packs') + ' ripped, ' + hits + (hits === 1 ? ' hit.' : ' hits.');
    }

    /* ---------- audio (Web Audio API: decode once, play sample-accurate with
       zero latency — HTMLAudioElement lagged on mobile). Buffers decode up-front
       so a hit fires the instant a card flips. ---------- */
    var audioOn = stats.sound !== false; /* default on; context resumes on first gesture */
    /* master level (owner, 7 Oct 2026: "a bit too loud"). The files are mastered like music (~-15 LUFS), so the
       hits played at ~-16 to -18 LUFS, near song loudness. 0.45 (~-7 dB) puts the loudest moments near -23 LUFS /
       -12 dBFS peak, the usual level for UI sound, and keeps the balance between sounds; louder = device volume.
       Applied where each gain is set, never as a node in the graph (see SAFARI AUDIO below). */
    var MASTER = 0.45;
    var actx = null;
    var buffers = {};   /* name -> AudioBuffer (or null while pending) */
    function ensureCtx() {
      if (!actx) {
        try { var AC = window.AudioContext || window.webkitAudioContext; if (AC) actx = new AC(); } catch (e) { actx = null; }
      }
      if (actx && actx.state === 'suspended' && actx.resume) { try { actx.resume(); } catch (e) { /* noop */ } }
      return actx;
    }
    /* iOS 16.4+ (WebKit): a Web-Audio-only page defaults to the 'ambient' audio
       session, which the hardware mute/silent switch silences — so SFX are dead
       unless the user flips ringer-off. Claiming the 'playback' session routes our
       audio through the media channel so it plays with the switch on silent. Do it
       only on a real user gesture, and only when our sound is ON, so we never seize
       a visitor's background music just for landing on the page. Feature-detected —
       a harmless no-op on Android / older Safari. (WebKit bug 237322.)
       Trade-off: 'playback' is non-mixable and pauses other apps' audio; we hand it
       back on mute via releaseAudioSession(). */
    function armAudio() {
      if (!audioOn) return;
      try { if (navigator.audioSession) navigator.audioSession.type = 'playback'; } catch (e) { /* type read-only on this build */ }
      ensureCtx();
    }
    function releaseAudioSession() {
      try { if (navigator.audioSession) navigator.audioSession.type = 'auto'; } catch (e) { /* noop */ }
    }
    /* SAFARI AUDIO — history, do not repeat: v9 resumed the ctx on every
       pointerdown (resume storm) and v10 ran a muted 1-sample looping source
       as a keep-alive — BOTH killed Safari desktop audio outright (WebKit
       stalls on sub-quantum loop buffers; Chrome tolerates them). The only
       safe shape found: plain gesture-gated ensureCtx() plus resume-then-fire
       in playSfx below, which never touches the graph outside a play call. */
    function loadBuffer(name) {
      if (!cfg.sfx || !cfg.sfx[name] || buffers[name] !== undefined) return;
      var ctx = ensureCtx();
      if (!ctx) return;
      buffers[name] = null; /* mark pending */
      fetch(cfg.sfx[name]).then(function (r) { return r.arrayBuffer(); })
        .then(function (ab) {
          return new Promise(function (res, rej) {
            var p = ctx.decodeAudioData(ab, res, rej); /* callback form for older Safari */
            if (p && p.then) p.then(res, rej);
          });
        })
        .then(function (buf) { buffers[name] = buf; })
        .catch(function () { buffers[name] = undefined; }); /* allow retry */
    }
    function preloadSfx() { ['tearLoop', 'tearRip', 'flip', 'rare', 'mega', 'god', 'packGrab'].forEach(loadBuffer); }
    function playSfx(name, vol) {
      if (!audioOn) return;
      var ctx = ensureCtx();
      if (!ctx) return;
      var buf = buffers[name];
      if (!buf) { loadBuffer(name); return; } /* not decoded yet — skip rather than lag */
      var fire = function () {
        try {
          var src = ctx.createBufferSource(); src.buffer = buf;
          var g = ctx.createGain(); g.gain.value = (vol == null ? 0.6 : vol) * MASTER;
          src.connect(g); g.connect(ctx.destination); src.start(0);
        } catch (e) { /* noop */ }
      };
      /* Safari re-suspends the ctx between sounds; starting a source into a
         suspended ctx queues it to burst out late. Fire after the resume
         completes instead (Chrome is virtually always 'running' -> sync). */
      if (ctx.state === 'running' || !ctx.resume) { fire(); return; }
      try {
        var p = ctx.resume();
        if (p && p.then) p.then(fire, function () { /* noop */ });
        else fire();
      } catch (e) { fire(); }
    }

    /* tear crinkle loops while dragging; gain tracks drag speed so it scrubs in sync */
    var tearSource = null, tearGain = null;
    function startTearAudio() {
      if (!audioOn) return;
      var ctx = ensureCtx();
      if (!ctx) return;
      var buf = buffers.tearLoop;
      if (!buf) { loadBuffer('tearLoop'); return; }
      try {
        stopTearAudio();
        tearSource = ctx.createBufferSource(); tearSource.buffer = buf; tearSource.loop = true;
        tearGain = ctx.createGain(); tearGain.gain.value = 0;
        tearSource.connect(tearGain); tearGain.connect(ctx.destination); tearSource.start(0);
      } catch (e) { /* noop */ }
    }
    function updateTearAudio(speed) {
      if (!tearGain || !actx) return;
      var v = Math.min(0.7, 0.12 + speed * 0.02) * MASTER;
      try { tearGain.gain.setTargetAtTime(audioOn ? v : 0, actx.currentTime, 0.02); } catch (e) { tearGain.gain.value = audioOn ? v : 0; }
    }
    function stopTearAudio() {
      if (tearSource) { try { tearSource.stop(); } catch (e) { /* noop */ } tearSource = null; tearGain = null; }
    }
    function applySoundUI() {
      els.sound.classList.toggle('is-on', audioOn);
      els.sound.setAttribute('aria-pressed', String(audioOn));
      els.sound.setAttribute('aria-label', audioOn ? 'Sound on' : 'Sound off');
    }

    /* ---------- phase switching ---------- */
    function show(el, on) { el.hidden = !on; }
    function setPhase(phase) {
      state.phase = phase;
      root.setAttribute('data-ps-phase', phase);
      show(els.config, phase === 'config');
      show(els.fallback, phase === 'fallback');
      show(els.stage, phase === 'tearing' || phase === 'revealing' || phase === 'packsum' || phase === 'session');
      show(els.pack, phase === 'tearing');
      show(els.open, phase === 'tearing');
      show(els.hint, phase === 'tearing');
      show(els.cards, phase === 'revealing');
      show(els.controls, phase === 'revealing');
      show(els.packsum, phase === 'packsum');
      show(els.session, phase === 'session');
      show(els.restart, phase !== 'config' && phase !== 'fallback');
      show(els.rail, phase === 'revealing' || phase === 'packsum');
    }

    /* ---------- data loading ---------- */
    function fetchSet(setInfo, attempt) {
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = ctrl && setTimeout(function () { ctrl.abort(); }, 8000);
      return fetch(setInfo.asset, ctrl ? { signal: ctrl.signal } : {})
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (json) { if (timer) clearTimeout(timer); return json; })
        .catch(function (err) {
          if (timer) clearTimeout(timer);
          if (attempt < 1) return fetchSet(setInfo, attempt + 1);
          throw err;
        });
    }
    function loadSet(setId) {
      if (dataCache[setId]) return Promise.resolve(dataCache[setId]);
      var info = null;
      for (var i = 0; i < cfg.sets.length; i++) if (cfg.sets[i].id === setId) info = cfg.sets[i];
      return fetchSet(info, 0).then(function (json) {
        dataCache[setId] = { data: json, pools: Engine.buildPools(json) };
        return dataCache[setId];
      });
    }

    /* ---------- image helpers ---------- */
    /* JP sets (tcgplayer-cdn) key images by productId, not card number; the
       card's optional pid (4th data element) wins when present. A full per-card
       URL override (card.img — JP paired-Stadium halves on Shopify Files)
       beats both templates and serves small + hires alike. */
    function glowCls(tier) { var g = Engine.glowFor(tier); return g ? ' is-' + g : ''; }
    /* the site-wide chase-card hover (snippets/evol-card-tilt: pointer tilt + glare, owner 28 Sep):
       the tilt layer wraps only the card image so the glare keeps the printed corner */
    function tiltWrap(img) {
      var t = document.createElement('span');
      t.className = 'evol-ps-tilt';
      t.setAttribute('data-evol-tilt', '');
      t.appendChild(img);
      return t;
    }
    /* merged sets (a gallery subset folded in: Trainer Gallery, Galarian
       Gallery, Shiny Vault) carry the SOURCE set id as the 4th element, and
       their image template wants "<source>/<number>" */
    function cardKey(data, num, fourth) { return data.merged ? fourth + '/' + num : (fourth !== undefined ? fourth : num); }
    function imgUrl(data, card) { return card.img || data.imgSmall.replace('{n}', cardKey(data, card.num, card.pid)); }
    function hiresUrl(data, card) { return card.img || data.imgLarge.replace('{n}', cardKey(data, card.num, card.pid)); }
    function preloadPack(pack) {
      if (!pack) return;
      for (var i = 0; i < pack.cards.length; i++) {
        var im = new Image();
        im.src = imgUrl(state.data, pack.cards[i]);
      }
    }

    /* ---------- card stack ---------- */
    function buildCardNodes(count) {
      /* EN packs deal 10 cards, JP packs (Storm Emeralda m6+) deal 5 */
      els.cards.textContent = '';
      cardNodes = [];
      for (var i = 0; i < (count || 10); i++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'evol-ps-card';
        btn.setAttribute('aria-label', 'Flip card');
        var inner = document.createElement('span');
        inner.className = 'evol-ps-card__inner';
        var back = document.createElement('span');
        back.className = 'evol-ps-card__back';
        var front = document.createElement('span');
        front.className = 'evol-ps-card__front';
        var img = document.createElement('img');
        img.alt = '';
        img.loading = 'eager';
        img.width = 245; img.height = 342;
        img.addEventListener('error', function (ev) {
          ev.target.closest('.evol-ps-card').classList.add('is-imgfail');
        });
        var cap = document.createElement('span');
        cap.className = 'evol-ps-card__cap';
        front.appendChild(img);
        front.appendChild(cap);
        inner.appendChild(back);
        inner.appendChild(front);
        btn.appendChild(inner);
        els.cards.appendChild(btn);
        cardNodes.push(btn);
      }
    }
    function dealPack(pack) {
      els.rail.textContent = '';
      state.flipIdx = 0;
      if (cardNodes.length !== pack.cards.length) buildCardNodes(pack.cards.length);
      for (var i = 0; i < cardNodes.length; i++) {
        var node = cardNodes[i];
        var card = pack.cards[i];
        node.classList.remove('is-flipped', 'is-cleared', 'is-imgfail');
        node.className = 'evol-ps-card tier-' + card.tier + (card.rev ? ' is-rev' : glowCls(card.tier));
        /* stack order: card 0 flips first => highest z */
        node.style.zIndex = String(20 - i);
        node.style.setProperty('--stk', String(Math.min(i, 4)));
        var img = node.querySelector('img');
        img.src = imgUrl(state.data, card);
        img.alt = card.name + ', ' + Engine.TIER_LABEL[card.rev ? 'rev' : card.tier];
        var cap = node.querySelector('.evol-ps-card__cap');
        var label = Engine.TIER_LABEL[card.rev ? 'rev' : card.tier];
        var val = state.data.values[card.num];
        cap.textContent = card.name + ' #' + card.num + ' · ' + label + (val && Engine.isHit(card) ? ' · ' + val : '');
      }
    }
    function railThumb(card) {
      var span = document.createElement('span');
      span.className = 'evol-ps-thumb tier-' + card.tier + (card.rev ? ' is-rev' : '') +
        (Engine.isHit(card) ? ' is-hit evol-chase-card' + glowCls(card.tier) : '');
      var img = document.createElement('img');
      img.src = imgUrl(state.data, card);
      img.alt = card.name;
      img.loading = 'lazy';
      img.width = 60; img.height = 84;
      span.appendChild(img);
      if (Engine.isHit(card)) {
        span.setAttribute('data-full', hiresUrl(state.data, card));
        var val = state.data.values[card.num];
        span.setAttribute('data-caption', card.name + ' #' + card.num + ' · ' +
          Engine.TIER_LABEL[card.tier] + (val ? ' · ' + val : ''));
      }
      els.rail.appendChild(span);
    }

    /* ---------- tear gesture ---------- */
    var tear = { active: false, pointerId: null, startX: 0, baseP: 0, raf: 0, pendingP: 0, milestone: 0 };
    function applyTear(p) {
      state.tearP = p;
      els.pack.style.setProperty('--tp', String(p));
      var glow = p < 0.7 ? 0 : (p - 0.7) / 0.3;
      els.glow.style.opacity = String(Math.min(glow, 1));
      if ('vibrate' in navigator) {
        var m = Math.floor(p * 4);
        if (m > tear.milestone) { tear.milestone = m; try { navigator.vibrate(10); } catch (err) { /* noop */ } }
      }
      if (p >= 0.85) finishTear();
    }
    function scheduleTear(p) {
      tear.pendingP = p;
      if (!tear.raf) {
        tear.raf = requestAnimationFrame(function () {
          tear.raf = 0;
          applyTear(tear.pendingP);
        });
      }
    }
    function onPointerDown(ev) {
      if (state.phase !== 'tearing' || state.busy || reducedMotion) return;
      if (!ev.isPrimary) return;
      tear.active = true;
      tear.pointerId = ev.pointerId;
      tear.startX = ev.clientX;
      tear.baseP = state.tearP;
      tear.lastX = ev.clientX;
      tear.lastT = (window.performance && performance.now) ? performance.now() : Date.now();
      startTearAudio();
      try { els.pack.setPointerCapture(ev.pointerId); } catch (err) { /* noop */ }
      els.pack.classList.add('is-tearing');
    }
    function onPointerMove(ev) {
      if (!tear.active || ev.pointerId !== tear.pointerId) return;
      var rect = els.pack.getBoundingClientRect();
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      var dt = Math.max(1, now - tear.lastT);
      var speed = Math.abs(ev.clientX - tear.lastX) / dt; /* px per ms */
      tear.lastX = ev.clientX; tear.lastT = now;
      updateTearAudio(speed);
      var p = tear.baseP + Math.abs(ev.clientX - tear.startX) / (rect.width * 0.85);
      scheduleTear(Math.max(state.tearP, Math.min(p, 1)));
    }
    function onPointerUp(ev) {
      if (!tear.active || ev.pointerId !== tear.pointerId) return;
      tear.active = false;
      els.pack.classList.remove('is-tearing');
      if (state.tearP < 0.85) { stopTearAudio(); els.pack.classList.add('is-settle'); }
      setTimeout(function () { els.pack.classList.remove('is-settle'); }, 260);
    }
    function finishTear() {
      if (state.busy || state.phase !== 'tearing') return;
      state.busy = true;
      tear.active = false;
      var pack = state.packs[state.packIdx];
      var tell = state.tells[state.packIdx];
      root.setAttribute('data-ps-tell', tell);
      els.pack.classList.add('is-open');
      stopTearAudio();
      playSfx('tearRip', 0.75);
      var delay = reducedMotion ? 60 : 620;
      setTimeout(function () {
        state.busy = false;
        dealPack(pack);
        setPhase('revealing');
        updateProgress();
        preloadPack(state.packs[state.packIdx + 1]);
      }, delay);
    }

    /* ---------- flow ---------- */
    function updateProgress() {
      els.progress.textContent = state.product.packs === 1
        ? state.data.name
        : state.data.name + ' · pack ' + (state.packIdx + 1) + ' of ' + state.product.packs;
    }
    function enterTear() {
      state.tearP = 0;
      tear.milestone = 0;
      state.busy = false;
      els.pack.classList.remove('is-open', 'is-settle', 'is-tearing');
      els.pack.style.setProperty('--tp', '0');
      els.glow.style.opacity = '0';
      var tell = state.tells[state.packIdx];
      root.setAttribute('data-ps-tell', tell);
      setPhase('tearing');
      updateProgress();
      els.hint.textContent = reducedMotion ? 'Press "Open pack"' : 'Tear across the foil to open';
      playSfx('packGrab', 0.5); /* quick crinkle as the pack lands on screen */
      /* first pack only: the room's config just collapsed, so bring the stage to the top */
      if (state.packIdx === 0) scrollStageToTop();
    }
    /* every phase after the first pack runs inside the room: bring its top into view */
    function scrollStageToTop() { els.room.scrollTop = 0; }
    /* accumulate the player's hits across the whole session (persists between packs) */
    function addSessionPull(card) {
      var span = document.createElement('span');
      span.className = 'evol-ps-thumb tier-' + card.tier + (card.rev ? ' is-rev' : '') + ' is-hit evol-chase-card' + glowCls(card.tier);
      var img = document.createElement('img');
      img.src = imgUrl(state.data, card); img.alt = card.name; img.loading = 'lazy';
      span.appendChild(img);
      span.setAttribute('data-full', hiresUrl(state.data, card));
      var val = state.data.values[card.num];
      span.setAttribute('data-caption', card.name + ' #' + card.num + ' · ' + Engine.TIER_LABEL[card.tier] + (val ? ' · ' + val : ''));
      els.yourpullsrow.appendChild(span);
      els.yourpulls.hidden = false;
    }

    function startProduct() {
      var rng = Math.random;
      els.yourpullsrow.textContent = '';
      els.yourpulls.hidden = true;
      state.packs = Engine.openProduct(state.data, state.pools, state.product.packs, rng);
      state.tells = state.packs.map(Engine.tellFor);
      state.packIdx = 0;
      state.session = { hits: {}, god: 0, best: null, packs: state.product.packs };
      for (var i = 0; i < state.packs.length; i++) {
        var pk = state.packs[i];
        if (pk.god) state.session.god++;
        for (var c = 0; c < pk.cards.length; c++) {
          var card = pk.cards[c];
          if (Engine.isHit(card)) {
            state.session.hits[card.tier] = (state.session.hits[card.tier] || 0) + 1;
            if (!state.session.best || Engine.TIER_RANK[card.tier] > Engine.TIER_RANK[state.session.best.tier]) {
              state.session.best = card;
            }
          }
        }
      }
      preloadPack(state.packs[0]);
      buildCardNodes(state.packs[0].cards.length);
      window.__evolSimDebug = { packs: state.packs, tells: state.tells, setId: state.setId, product: state.product };
      enterTear();
    }
    function flipNext() {
      if (state.phase !== 'revealing' || state.flipIdx >= cardNodes.length) return;
      var i = state.flipIdx;
      var pack = state.packs[state.packIdx];
      var node = cardNodes[i];
      node.classList.add('is-flipped');
      state.flipIdx++;
      if (!pack.god) {
        var fc = pack.cards[i];
        if (Engine.isHit(fc)) playSfx(Engine.glowFor(fc.tier) === 'gold' ? 'mega' : 'rare', 0.7);
        else playSfx('flip', 0.32);
      } else {
        playSfx('flip', 0.32);
      }
      var clearDelay = reducedMotion ? 220 : 700;
      (function (n, card, last) {
        setTimeout(function () {
          n.classList.add('is-cleared');
          railThumb(card);
          if (Engine.isHit(card)) addSessionPull(card);
          if (last) packSummary();
        }, clearDelay);
      })(node, pack.cards[i], state.flipIdx === cardNodes.length);
    }
    function revealAll() {
      if (state.phase !== 'revealing') return;
      var step = reducedMotion ? 60 : 150;
      (function tick() {
        if (state.phase !== 'revealing' || state.flipIdx >= cardNodes.length) return;
        flipNext();
        if (state.flipIdx < cardNodes.length) setTimeout(tick, step);
      })();
    }
    /* drop-alerts card (evol-tool-alerts): shown once, on the first pack summary of the visit,
       so a box opener isn't asked 36 times; the session summary carries its own card. */
    var toolAlerts = root.querySelector('[data-evol-tool-alerts]');
    var toolAlertsShown = false;
    function packSummary() {
      if (state.phase !== 'revealing') return;
      if (toolAlerts) { toolAlerts.hidden = toolAlertsShown; toolAlertsShown = true; }
      var pack = state.packs[state.packIdx];
      var hits = pack.cards.filter(Engine.isHit);
      els.packsumTitle.textContent = pack.god ? 'GOD PACK' :
        (hits.length ? hits.length + ' hit' + (hits.length > 1 ? 's' : '') + ' this pack' : 'No big hits this pack');
      els.packsumBody.textContent = '';
      hits.forEach(function (card) {
        var line = document.createElement('p');
        line.className = 'evol-ps-hitline tier-' + card.tier + glowCls(card.tier);
        var val = state.data.values[card.num];
        line.textContent = card.name + ' #' + card.num + ' · ' + Engine.TIER_LABEL[card.tier] + (val ? ' · ' + val : '');
        els.packsumBody.appendChild(line);
      });
      if (!state.data.noEnergy) { /* JP packs (m6+) ship no basic energy */
        var energy = document.createElement('p');
        energy.className = 'evol-ps-energy';
        energy.textContent = '+ 1 Basic Energy';
        els.packsumBody.appendChild(energy);
      }

      /* persist stats once per completed pack */
      stats.packs[state.setId] = (stats.packs[state.setId] || 0) + 1;
      var sh = stats.hits[state.setId] = stats.hits[state.setId] || {};
      hits.forEach(function (card) { sh[card.tier] = (sh[card.tier] || 0) + 1; });
      var curBest = stats.best[state.setId];
      hits.forEach(function (card) {
        if (!curBest || Engine.TIER_RANK[card.tier] > Engine.TIER_RANK[curBest.tier]) {
          curBest = { num: card.num, name: card.name, tier: card.tier };
        }
      });
      if (curBest) stats.best[state.setId] = curBest;
      saveStats(stats);

      /* peak-intent buy CTA: only after a pack that actually hit (or a god pack) —
         "you pulled a simulated one, here's the real thing". Never on dud packs, so
         a booster-box opener isn't nagged 36 times. Set-specific where a collection
         exists; falls back to shop-all for sets without one (e.g. Surging Sparks). */
      if (hits.length || pack.god) {
        els.packshop.href = state.data.shopUrl || cfg.shopAllUrl;
        els.packshop.textContent = state.data.shopUrl ? 'Shop real ' + state.data.name : 'Shop sealed Pokemon TCG';
        show(els.packshop, true);
      } else {
        show(els.packshop, false);
      }

      var last = state.packIdx === state.packs.length - 1;
      show(els.next, !last);
      show(els.finish, last);
      setPhase('packsum');
      if (pack.god) { root.setAttribute('data-ps-tell', 'gold'); playSfx('god', 0.85); }
    }
    function sessionSummary() {
      var s = state.session;
      els.sessionBody.textContent = '';
      els.sessionBest.textContent = '';
      var head = document.createElement('p');
      head.className = 'evol-ps-session__head';
      var totalHits = 0;
      Object.keys(s.hits).forEach(function (t) { totalHits += s.hits[t]; });
      head.textContent = s.packs + ' pack' + (s.packs > 1 ? 's' : '') + ' ripped, ' + totalHits + ' hit' + (totalHits === 1 ? '' : 's') + (s.god ? ' + a GOD PACK' : '');
      els.sessionBody.appendChild(head);
      /* every hit tier the session drew, rarest first (any era's ladder) */
      Object.keys(s.hits).sort(function (a, b) { return Engine.TIER_RANK[b] - Engine.TIER_RANK[a]; }).forEach(function (t) {
        var line = document.createElement('p');
        line.className = 'evol-ps-hitline tier-' + t + glowCls(t);
        var exp = '';
        if (state.data.rates[t]) {
          var midOdds = (state.data.rates[t][0] + state.data.rates[t][1]) / 2;
          exp = ' (expected ~' + (Math.round(s.packs / midOdds * 10) / 10) + ')';
        }
        line.textContent = s.hits[t] + '× ' + Engine.TIER_LABEL[t] + exp;
        els.sessionBody.appendChild(line);
      });
      if (s.best) {
        var bestWrap = document.createElement('div');
        bestWrap.className = 'evol-ps-best';
        var label = document.createElement('p');
        label.textContent = 'Best pull:';
        var thumb = document.createElement('span');
        thumb.className = 'evol-ps-best__card evol-chase-card tier-' + s.best.tier + glowCls(s.best.tier);
        thumb.setAttribute('data-full', hiresUrl(state.data, s.best));
        var val = state.data.values[s.best.num];
        thumb.setAttribute('data-caption', s.best.name + ' #' + s.best.num + ' · ' + Engine.TIER_LABEL[s.best.tier] + (val ? ' · ' + val : ''));
        var img = document.createElement('img');
        img.src = imgUrl(state.data, s.best);
        img.alt = s.best.name;
        img.width = 180; img.height = 251;
        thumb.appendChild(tiltWrap(img));
        bestWrap.appendChild(label);
        bestWrap.appendChild(thumb);
        els.sessionBest.appendChild(bestWrap); /* rendered beside the actions on desktop */
      } else {
        var none = document.createElement('p');
        none.className = 'evol-ps-session__none';
        none.textContent = 'No Double Rare or better this time. That’s variance for you: most packs are like this.';
        els.sessionBody.appendChild(none);
      }
      els.shop.href = state.data.shopUrl || cfg.shopAllUrl;
      els.shop.textContent = state.data.shopUrl ? 'Shop ' + state.data.name : 'Shop sealed Pokemon TCG';
      show(els.guide, !!state.data.guideUrl);
      if (state.data.guideUrl) els.guide.href = state.data.guideUrl;
      renderAlltime();
      /* Share only when there's a real pull worth showing off — a "0 hits" card
         isn't something people share (owner call). Warm its art so the tap is
         instant and lands inside the Web Share gesture-activation window. */
      show(els.share, !!s.best);
      if (s.best) preloadShareAssets();
      /* The drop-alert CTA in the session summary is a plain [data-evol-alerts] link:
         a document-delegated handler in evol-custom.js opens the store's existing
         Omnisend drop-alerts popup (email + phone + SMS consent), falling back to
         /pages/notify. Nothing to wire here — it's pure markup. */
      setPhase('session');
    }

    /* ---------- share your pull (canvas -> PNG -> Web Share / download) ----------
       Card art is cross-origin (images.scrydex.com / images.pokemontcg.io, both
       send Access-Control-Allow-Origin:*). Drawing a cross-origin <img> would TAINT
       the canvas and block toBlob(); instead we fetch each image as a CORS blob and
       decode it to an ImageBitmap — same-origin pixel data, so the canvas stays
       clean and toBlob() succeeds. Same pipeline works for our same-origin assets. */
    var SHARE_W = 1080, SHARE_H = 1350;
    var SHARE_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
    var TOOL_URL = (location.origin || 'https://evolvault.com') + location.pathname;
    var shareState = { building: false, blob: null, file: null, objurl: null, cardBmpP: null, brandBmpP: null };

    function loadBitmap(url) {
      if (!url) return Promise.resolve(null);
      /* cache:'reload' is load-bearing: the lightbox loads the SAME hires url as a
         plain no-cors <img>, and images.pokemontcg.io answers Origin-less requests
         with no ACAO header and no Vary:Origin — so once a user zooms a pull, the
         poisoned cache entry makes a normal CORS fetch of that url fail and the
         share card renders without the card art. 'reload' always goes to network
         (with Origin -> ACAO granted) and also heals already-poisoned caches. */
      return fetch(url, { mode: 'cors', credentials: 'omit', cache: 'reload' })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); })
        .then(function (b) {
          if (window.createImageBitmap) return createImageBitmap(b);
          return new Promise(function (res) {
            var u = URL.createObjectURL(b), im = new Image();
            im.onload = function () { res(im); };
            im.onerror = function () { res(null); };
            im.src = u;
          });
        })
        .catch(function () { return null; });
    }
    /* Warm the images the share card draws. Always use the HI-RES card url
       (hiresUrl -> imgLarge). NB the "only small urls get cache-poisoned" assumption
       was wrong — the lightbox no-cors <img> poisons HIRES urls too (owner hit it
       2026-08-20: shared card had no card art after zooming the pull) — which is why
       loadBitmap fetches with cache:'reload'. If any fetch still fails, loadBitmap
       resolves null and the card renders without that image; the canvas never taints. */
    function preloadShareAssets() {
      var s = state.session;
      shareState.blob = null; shareState.file = null;
      shareState.cardBmpP = (s && s.best)
        ? loadBitmap(hiresUrl(state.data, s.best))
        : loadBitmap(setCfg(state.setId).pack);
      if (!shareState.brandBmpP) shareState.brandBmpP = loadBitmap(cfg.brand);
      /* eagerly render the PNG so the Share tap can call navigator.share() synchronously */
      buildShareCard().then(function (blob) {
        if (!blob) return;
        shareState.blob = blob;
        try { shareState.file = new File([blob], 'evol-vault-pull.png', { type: 'image/png' }); } catch (e) { shareState.file = null; }
      });
    }
    function tierShareColor(t) {
      return { gold: '#ffd27a', purple: '#a99cf5', blue: '#7fb0ff' }[Engine.glowFor(t)] || '#ffffff';
    }
    function roundRectPath(ctx, x, y, w, h, r) {
      r = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }
    /* wrap a card name to at most maxLines, ellipsising the last if it overflows */
    function wrapLines(ctx, text, maxW, maxLines) {
      var words = String(text).split(/\s+/), lines = [], cur = '';
      for (var i = 0; i < words.length; i++) {
        var test = cur ? cur + ' ' + words[i] : words[i];
        if (ctx.measureText(test).width <= maxW || !cur) { cur = test; }
        else { lines.push(cur); cur = words[i]; if (lines.length === maxLines) { cur = ''; break; } }
      }
      if (cur && lines.length < maxLines) lines.push(cur);
      if (lines.join(' ').length < text.length) {
        var last = lines.length ? lines[lines.length - 1] : '';
        while (last && ctx.measureText(last + '…').width > maxW) last = last.replace(/\s?\S+$/, '');
        lines[lines.length - 1] = (last || (lines[lines.length - 1] || '')) + '…';
      }
      return lines;
    }
    function dataURLtoBlob(durl) {
      var parts = durl.split(','), mime = parts[0].match(/:(.*?);/)[1];
      var bin = atob(parts[1]), n = bin.length, u8 = new Uint8Array(n);
      while (n--) u8[n] = bin.charCodeAt(n);
      return new Blob([u8], { type: mime });
    }
    function buildShareCard() {
      var s = state.session;
      return Promise.all([
        shareState.cardBmpP || Promise.resolve(null),
        shareState.brandBmpP || Promise.resolve(null)
      ]).then(function (r) {
        var cardBmp = r[0], brandBmp = r[1], hasHit = !!(s && s.best);
        var cv = document.createElement('canvas');
        cv.width = SHARE_W; cv.height = SHARE_H;
        var ctx = cv.getContext('2d');
        ctx.textBaseline = 'alphabetic';

        ctx.fillStyle = '#000'; ctx.fillRect(0, 0, SHARE_W, SHARE_H);

        /* honesty marker — a shared image must never read as a real pull. Fixed
           top-right corner, independent of the centred content block. */
        ctx.textBaseline = 'top'; ctx.textAlign = 'right';
        ctx.font = '600 20px ' + SHARE_FONT; ctx.fillStyle = 'rgba(255,255,255,0.32)';
        ctx.fillText('SIMULATED', SHARE_W - 64, 58);

        /* measure the name up-front, then lay the whole composition out and vertically
           centre it so the card is never top-weighted with dead space beneath it */
        ctx.textAlign = 'center';
        ctx.font = '700 52px ' + SHARE_FONT;
        var nameLines = hasHit ? wrapLines(ctx, s.best.name, 900, 2) : ['No hits this time'];
        var brandH = brandBmp ? 280 * (brandBmp.height / brandBmp.width) : 56;
        var ratio = hasHit ? 245 / 342 : 351 / 640;
        var ch = 600, cw = ch * ratio;
        if (cw > 440) { cw = 440; ch = cw / ratio; }
        var NAME_LH = 60, GAP_SUB = 8, SUB_H = 40, GAP_DIV = 28, DIV_AFTER = 32,
          STAT_H = 42, GAP_SET = 6, SET_H = 36, GAP_URL = 40, URL_H = 34;
        var textH = nameLines.length * NAME_LH + GAP_SUB + SUB_H + GAP_DIV + 2 + DIV_AFTER +
          STAT_H + GAP_SET + SET_H + GAP_URL + URL_H;
        var totalH = brandH + 52 + ch + 56 + textH;
        var y = Math.max(70, (SHARE_H - totalH) / 2);

        /* brand lockup (geometric lion + wordmark) */
        if (brandBmp) {
          ctx.globalAlpha = 0.96;
          ctx.drawImage(brandBmp, (SHARE_W - 280) / 2, y, 280, brandH);
          ctx.globalAlpha = 1;
        } else {
          ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.font = '800 46px ' + SHARE_FONT;
          ctx.fillText('EVOL VΛULT', SHARE_W / 2, y);
        }
        y += brandH + 52;

        /* card / pack art with purple glow + rounded corners */
        var cx = (SHARE_W - cw) / 2, cy = y, rad = hasHit ? 22 : 14;
        var glow = ctx.createRadialGradient(SHARE_W / 2, cy + ch / 2, 40, SHARE_W / 2, cy + ch / 2, cw);
        glow.addColorStop(0, 'rgba(111,93,236,0.42)');
        glow.addColorStop(1, 'rgba(111,93,236,0)');
        ctx.fillStyle = glow; ctx.fillRect(0, cy - 70, SHARE_W, ch + 140);
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 40; ctx.shadowOffsetY = 22;
        roundRectPath(ctx, cx, cy, cw, ch, rad); ctx.fillStyle = '#0c0a12'; ctx.fill();
        ctx.restore();
        if (cardBmp) {
          ctx.save(); roundRectPath(ctx, cx, cy, cw, ch, rad); ctx.clip();
          ctx.drawImage(cardBmp, cx, cy, cw, ch); ctx.restore();
        }
        y += ch + 56;

        /* name + tier (hit) / subtitle (no-hit) */
        ctx.fillStyle = '#ffffff'; ctx.font = '700 52px ' + SHARE_FONT;
        for (var i = 0; i < nameLines.length; i++) { ctx.fillText(nameLines[i], SHARE_W / 2, y); y += NAME_LH; }
        y += GAP_SUB;
        if (hasHit) {
          ctx.font = '600 32px ' + SHARE_FONT; ctx.fillStyle = tierShareColor(s.best.tier);
          ctx.fillText(Engine.TIER_LABEL[s.best.tier], SHARE_W / 2, y);
        } else {
          ctx.font = '400 30px ' + SHARE_FONT; ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.fillText('That’s the real odds.', SHARE_W / 2, y);
        }
        y += SUB_H + GAP_DIV;

        /* divider */
        ctx.strokeStyle = 'rgba(111,93,236,0.6)'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(SHARE_W / 2 - 120, y); ctx.lineTo(SHARE_W / 2 + 120, y); ctx.stroke();
        y += 2 + DIV_AFTER;

        /* stat line + set name */
        var totalHits = 0;
        if (s) Object.keys(s.hits).forEach(function (t) { totalHits += s.hits[t]; });
        var stat = totalHits + (totalHits === 1 ? ' hit' : ' hits') + ' · ' +
          s.packs + (s.packs === 1 ? ' pack' : ' packs') + (s.god ? ' · GOD PACK' : '');
        ctx.font = '600 32px ' + SHARE_FONT; ctx.fillStyle = 'rgba(255,255,255,0.86)';
        ctx.fillText(stat, SHARE_W / 2, y); y += STAT_H + GAP_SET;
        ctx.font = '500 28px ' + SHARE_FONT; ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText(state.data.name, SHARE_W / 2, y); y += SET_H + GAP_URL;

        /* url */
        ctx.font = '600 28px ' + SHARE_FONT; ctx.fillStyle = '#8877f0';
        ctx.fillText('evolvault.com', SHARE_W / 2, y);

        return new Promise(function (resolve) {
          try {
            if (cv.toBlob) cv.toBlob(function (b) { resolve(b); }, 'image/png');
            else resolve(dataURLtoBlob(cv.toDataURL('image/png')));
          } catch (e) { resolve(null); }
        });
      });
    }

    /* ---------- events ---------- */
    /* claim the audible-in-silent-mode audio session the moment the user commits
       to opening (earliest real gesture that precedes any SFX) */
    els.pack.addEventListener('pointerdown', armAudio);
    els.pack.addEventListener('pointerdown', onPointerDown);
    els.pack.addEventListener('pointermove', onPointerMove);
    els.pack.addEventListener('pointerup', onPointerUp);
    els.pack.addEventListener('pointercancel', onPointerUp);
    els.open.addEventListener('click', function () { if (state.phase === 'tearing' && !state.busy) { applyTear(1); } });
    els.flipnext.addEventListener('click', flipNext);
    els.flipall.addEventListener('click', revealAll);
    els.cards.addEventListener('click', function (ev) {
      if (ev.target.closest('.evol-ps-card')) flipNext();
    });
    els.next.addEventListener('click', function () { state.packIdx++; enterTear(); });
    els.finish.addEventListener('click', sessionSummary);
    els.restart.addEventListener('click', function () { setPhase('config'); renderAlltime(); });
    els.retry.addEventListener('click', function () { setPhase('config'); });
    els.again.addEventListener('click', function () { startProduct(); });
    els.resetstats.addEventListener('click', function () {
      stats = { v: 1, packs: {}, hits: {}, best: {} };
      saveStats(stats);
      renderAlltime();
    });
    els.sets.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-set]');
      if (!btn) return;
      if (state.phase !== 'config' && state.phase !== 'fallback') { stopTearAudio(); setPhase('config'); renderAlltime(); }
      state.setId = btn.getAttribute('data-set');
      renderSetSelection();
      openRoom(true);
    });

    /* iOS-safe background scroll lock while the room is open: pin the body with
       position:fixed at the current offset, restore it on close */
    var scrollLockY = 0;
    function lockScroll(on) {
      if (on) {
        scrollLockY = window.pageYOffset || window.scrollY || 0;
        document.body.style.top = (-scrollLockY) + 'px';
        document.body.classList.add('evol-ps-noscroll');
      } else if (document.body.classList.contains('evol-ps-noscroll')) {
        document.body.classList.remove('evol-ps-noscroll');
        document.body.style.top = '';
        window.scrollTo(0, scrollLockY);
      }
    }
    /* Share your pull. The card is pre-built in preloadShareAssets(), so the tap can
       invoke navigator.share() SYNCHRONOUSLY — Web Share needs transient activation,
       and any async gap between the tap and share() makes iOS/Safari silently reject
       it (that was the "Share does nothing" bug). Native share sheet where the platform
       can share the file (iOS/Android/macOS/Win); a direct PNG download otherwise. */
    function downloadShareCard() {
      if (!shareState.blob) return;
      var u = URL.createObjectURL(shareState.blob);
      var a = document.createElement('a');
      a.href = u; a.download = 'evol-vault-pull.png';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(u); }, 4000);
    }
    function shareText() {
      return 'My simulated ' + state.data.name + ' pull. Open your own free pack simulator:';
    }
    /* The native share sheet is only reliable on MOBILE. On desktop, navigator.share
       with a file drops the caption and behaves inconsistently (owner saw a duplicated
       card + missing message), so desktop never calls it — it saves the card and copies
       the caption instead. */
    function isProbablyMobile() {
      try { if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') return navigator.userAgentData.mobile; } catch (e) { /* older build */ }
      return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
    }
    function legacyCopy(str) {
      try {
        var ta = document.createElement('textarea');
        ta.value = str; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      } catch (e) { /* noop */ }
    }
    function copyText(str) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(str).catch(function () { legacyCopy(str); });
      }
      legacyCopy(str); return Promise.resolve();
    }
    /* transient non-blocking confirmation (not a modal) */
    function showToast(msg) {
      var t = document.createElement('div');
      t.className = 'evol-ps-toast'; t.setAttribute('role', 'status'); t.textContent = msg;
      document.body.appendChild(t);
      requestAnimationFrame(function () { t.classList.add('is-in'); });
      setTimeout(function () {
        t.classList.remove('is-in');
        setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 320);
      }, 3400);
    }
    /* desktop / no-native path: save the card ONCE + copy the caption so the message
       comes through too, with a small confirmation toast */
    function desktopShare() {
      copyText(shareText() + ' ' + TOOL_URL);
      downloadShareCard();
      showToast('Card saved to your downloads. Caption copied, paste it in your post.');
    }
    function shareCardNow() {
      var file = shareState.file;
      var canNative = file && navigator.canShare && navigator.share && navigator.canShare({ files: [file] });
      if (canNative && isProbablyMobile()) {
        navigator.share({
          files: [file],
          title: 'Evol Vault — simulated pull',
          text: shareText(),
          url: TOOL_URL
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return; /* user dismissed the sheet */
          desktopShare(); /* native share failed at runtime -> deterministic fallback */
        });
      } else {
        desktopShare();
      }
    }
    els.share.addEventListener('click', function () {
      /* fast path: card already built -> share synchronously (preserves the tap's activation) */
      if (shareState.file || shareState.blob) { shareCardNow(); return; }
      /* not ready yet (rare) -> build then act; may lose activation on strict browsers,
         so shareCardNow() falls back to a download there */
      if (shareState.building) return;
      shareState.building = true;
      els.share.classList.add('is-loading');
      els.share.setAttribute('aria-busy', 'true');
      buildShareCard().then(function (blob) {
        els.share.classList.remove('is-loading');
        els.share.removeAttribute('aria-busy');
        shareState.building = false;
        if (!blob) return;
        shareState.blob = blob;
        try { shareState.file = new File([blob], 'evol-vault-pull.png', { type: 'image/png' }); } catch (e) { shareState.file = null; }
        shareCardNow();
      }).catch(function () {
        els.share.classList.remove('is-loading');
        els.share.removeAttribute('aria-busy');
        shareState.building = false;
      });
    });
    els.products.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-kind]');
      if (!btn) return;
      var key = btn.getAttribute('data-kind');
      var sc = setCfg(state.setId);
      for (var i = 0; i < sc.products.length; i++) {
        if (sc.products[i].key === key) state.product = sc.products[i];
      }
      els.products.querySelectorAll('[data-kind]').forEach(function (t) {
        t.setAttribute('aria-pressed', String(t.getAttribute('data-kind') === key));
      });
      if (els.ladder && dataCache[state.setId]) renderLadder(sc, dataCache[state.setId].data);
    });
    els.sound.addEventListener('click', function () {
      audioOn = !audioOn;
      stats.sound = audioOn;
      saveStats(stats);
      applySoundUI();
      if (audioOn) { armAudio(); preloadSfx(); playSfx('flip', 0.4); }
      else { releaseAudioSession(); }
    });
    els.start.addEventListener('click', function () {
      if (state.busy) return;
      state.busy = true;
      armAudio();
      preloadSfx();
      els.start.classList.add('is-loading');
      loadSet(state.setId).then(function (entry) {
        state.data = entry.data;
        state.pools = entry.pools;
        state.busy = false;
        els.start.classList.remove('is-loading');
        startProduct();
      }).catch(function () {
        state.busy = false;
        els.start.classList.remove('is-loading');
        setPhase('fallback');
      });
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape' || !roomOpen) return;
      /* the store's card lightbox sits above the room and closes on Escape itself */
      var lb = document.getElementById('evol-lightbox');
      if (lb && lb.getAttribute('aria-hidden') === 'false') return;
      requestCloseRoom();
    });
    root.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      if (ev.target.closest('button, select, a')) return;
      if (state.phase === 'revealing') { ev.preventDefault(); flipNext(); }
    });

    /* ---------- the wall: era filter + search ---------- */
    var tiles = Array.prototype.slice.call(els.sets.querySelectorAll('[data-set]'));
    /* wall packs after the first five load about 600 px before they scroll into view (the section
       renders their sources as data-ps-src / data-ps-srcset); the era filter and search bring
       tiles into view the same way */
    (function lazyTiles() {
      var imgs = Array.prototype.slice.call(els.sets.querySelectorAll('img[data-ps-srcset]'));
      function load(img) {
        img.srcset = img.getAttribute('data-ps-srcset');
        img.src = img.getAttribute('data-ps-src');
        img.removeAttribute('data-ps-srcset');
        img.removeAttribute('data-ps-src');
      }
      if (!('IntersectionObserver' in window)) { imgs.forEach(load); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); load(e.target); } });
      }, { rootMargin: '600px 0px' });
      imgs.forEach(function (img) { io.observe(img); });
    })();
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-ps-group]'));
    function syncGroups() {
      groups.forEach(function (g) { g.hidden = !g.querySelector('[data-set]:not([hidden])'); });
      if (els.searchempty) els.searchempty.hidden = tiles.some(function (t) { return !t.hidden; });
    }
    function setEra(era) {
      if (els.search && els.search.value) els.search.value = '';
      els.eras.querySelectorAll('[data-era]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-era') === era)); });
      tiles.forEach(function (t) { t.hidden = era !== 'all' && t.getAttribute('data-era') !== era; });
      syncGroups();
    }
    if (els.eras) els.eras.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-era]');
      if (b) setEra(b.getAttribute('data-era'));
    });
    if (els.search) els.search.addEventListener('input', function () {
      var q = els.search.value.trim().toLowerCase();
      if (els.eras) els.eras.querySelectorAll('[data-era]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-era') === 'all')); });
      tiles.forEach(function (t) { t.hidden = !!q && (t.getAttribute('data-q') || t.getAttribute('data-name') || '').toLowerCase().indexOf(q) === -1; });
      syncGroups();
    });

    /* ---------- the opening room ----------
       A tile pushes a history entry (?set=<id>) so the browser back button
       (and the Android back gesture) closes the room instead of leaving the
       page. A room opened from a deep link (?set= on arrival) pushes nothing:
       back then leaves for the page that linked here, as a visitor expects. */
    var roomOpen = false, roomPushed = false, roomFrom = null;
    /* focus still moves into the room and back to its pack (keyboard and screen-reader
       users land where they were), but a mouse or touch user gets no ring for it: Escape
       counts as a keyboard press, so the browser would draw one (owner, 3 Oct). kbNav =
       the visitor has moved focus with Tab since their last click or tap */
    var kbNav = false;
    document.addEventListener('keydown', function (ev) { if (ev.key === 'Tab') kbNav = true; }, true);
    document.addEventListener('pointerdown', function () { kbNav = false; }, true);
    function moveFocus(el) {
      if (!el || !el.focus || el === document.body) return;
      if (!kbNav && el.classList) {
        el.classList.add('is-quietfocus');
        el.addEventListener('blur', function () { el.classList.remove('is-quietfocus'); }, { once: true });
      }
      try { el.focus({ preventScroll: true }); } catch (e) { /* old browser */ }
    }
    function urlWithSet(id) {
      try {
        var u = new URL(location.href);
        if (id) u.searchParams.set('set', id); else u.searchParams.delete('set');
        return u.pathname + u.search + u.hash;
      } catch (e) { return null; }
    }
    function openRoom(push) {
      if (roomOpen) return;
      roomOpen = true;
      roomFrom = document.activeElement;
      els.room.hidden = false;
      root.classList.add('is-room-open');
      lockScroll(true);
      els.room.scrollTop = 0;
      if (push && window.history && history.pushState) {
        var url = urlWithSet(state.setId);
        if (url) { try { history.pushState({ evolRoom: state.setId }, '', url); roomPushed = true; } catch (e) { roomPushed = false; } }
      }
      moveFocus(els.roomclose);
    }
    function closeRoomUI() {
      if (!roomOpen) return;
      roomOpen = false;
      stopTearAudio();
      if (state.phase !== 'config') setPhase('config');
      renderAlltime();
      els.room.hidden = true;
      root.classList.remove('is-room-open');
      lockScroll(false);
      var tile = els.sets.querySelector('[data-set="' + state.setId + '"]');
      moveFocus(tile || roomFrom);
    }
    function requestCloseRoom() {
      if (!roomOpen) return;
      if (roomPushed && history.state && history.state.evolRoom) { roomPushed = false; history.back(); return; } /* popstate closes it */
      closeRoomUI();
      var url = urlWithSet(null);
      if (url && window.history && history.replaceState) { try { history.replaceState(null, '', url); } catch (e) { /* noop */ } }
    }
    els.roomclose.addEventListener('click', requestCloseRoom);
    window.addEventListener('popstate', function (ev) {
      if (roomOpen && !(ev.state && ev.state.evolRoom)) { roomPushed = false; closeRoomUI(); }
    });

    renderSetSelection();
    if (presetSet) openRoom(false); /* a set page linked here with its set: straight into its room */
    renderAlltime();
    applySoundUI();
    setPhase('config');
    /* decode SFX up-front (context starts suspended; resumes on first gesture) */
    try { preloadSfx(); } catch (e) { /* noop */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
